import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';

import { FormControl, FormField, FormItem, FormMessage, MultiSelect } from '@klareospace/pharos-lib-web-utils';
import type { MultiSelectOption } from '@klareospace/pharos-lib-web-utils';
import { BUILT_IN_ROLE_KEY } from '@klareospace/pharos-lib-types';
import { deduplicate } from '@klareospace/pharos-lib-utils';

import { SpaceSitesSelector } from './space-sites-selector';
import type { CarrierSiteFilter } from '../../carriers/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ScopeFieldsProps<T extends FieldValues> {
  /** Resolved labels for existing site scopes, so drifted sites stay visible via the escape hatch. */
  existingSiteOptions?: { label: string; parentSpaceId?: string; value: string }[];
  form: UseFormReturn<T>;
  handleSiteSelectionChange: (spaceId: string, siteIds: string[]) => void;
  i18nPrefix: string;
  isSiteLocked?: boolean;
  isSpaceLocked?: boolean;
  lockedSiteId?: string | null;
  selectedSpaceIds: string[];
  /**
   * Optional resolver returning the carrier-derived site constraint for a given space.
   * When provided, the site selector of each space only offers that carrier's enabled sites
   * (used by the driver scope selector). When omitted, all sites in the space are offered.
   */
  siteFilterForSpace?: (spaceId: string) => CarrierSiteFilter;
  spaceLabelMap: Map<string, string>;
  spaceOptions: MultiSelectOption[];
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ScopeFields<T extends FieldValues>({
  existingSiteOptions,
  form,
  handleSiteSelectionChange,
  i18nPrefix,
  isSiteLocked = false,
  isSpaceLocked = false,
  lockedSiteId,
  selectedSpaceIds,
  siteFilterForSpace,
  spaceLabelMap,
  spaceOptions,
}: ScopeFieldsProps<T>): React.JSX.Element {
  const { t } = useTranslation('settings');

  const watchedRoles: string[] = form.watch('roles' as Path<T>);
  const isTenantAdmin = watchedRoles.includes(BUILT_IN_ROLE_KEY.TENANT_ADMIN);

  // Live form siteIds — watched so that programmatic updates (e.g. carrier-change prune in
  // the driver drawer) are reflected immediately in the per-space MultiSelect chips. The form is
  // initialised with the driver's/user's saved site scopes (via getEditFormDefaults), so this
  // already contains the originally-saved sites on open — no union with existingSiteIds needed.
  const watchedSiteIds: string[] = form.watch('siteIds' as Path<T>) ?? [];

  // Render a site selector for every selected space, plus the parent spaces of any existing site
  // scopes — so a driver scoped only to sites (no space-level scope) still shows an editable sites
  // section. The parent spaces are for rendering only and are NOT added to the saved spaceIds,
  // so editing such a driver never grants broader space-level access.
  const spacesToRender = useMemo(() => {
    const parentSpaceIds = (existingSiteOptions ?? [])
      .map((option) => option.parentSpaceId)
      .filter((id): id is string => Boolean(id));
    return deduplicate([...selectedSpaceIds, ...parentSpaceIds]);
  }, [selectedSpaceIds, existingSiteOptions]);

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium leading-none">{t(`${i18nPrefix}.spaces`)}</p>
      {isTenantAdmin ? (
        <p className="text-sm text-muted-foreground">{t('users.scope.allSpaces')}</p>
      ) : (
        <>
          <FormField
            control={form.control}
            name={'spaceIds' as Path<T>}
            render={({ field }): React.JSX.Element => (
              <FormItem>
                <FormControl>
                  <MultiSelect
                    disabled={isSpaceLocked}
                    emptyText={t('users.multiSelect.noResults')}
                    hideFooter
                    label={t(`${i18nPrefix}.spacesLabel`)}
                    onValueChange={field.onChange}
                    options={spaceOptions}
                    placeholder={t(`${i18nPrefix}.spacesPlaceholder`)}
                    searchable
                    showLabelWhenFiltering
                    value={field.value ?? []}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {spacesToRender.length > 0 &&
            spacesToRender.map((spaceId) => {
              const spaceName = spaceLabelMap.get(spaceId) ?? spaceId;
              return (
                <SpaceSitesSelector
                  existingSiteOptions={existingSiteOptions}
                  isSiteLocked={isSiteLocked}
                  key={spaceId}
                  label={t(`${i18nPrefix}.sitesLabel`)}
                  lockedSiteId={lockedSiteId}
                  noResultsText={t('users.multiSelect.noResults')}
                  onSiteSelectionChange={(siteIds): void => handleSiteSelectionChange(spaceId, siteIds)}
                  placeholder={t(`${i18nPrefix}.sitesPlaceholder`)}
                  selectedSiteIds={watchedSiteIds}
                  siteFilter={siteFilterForSpace?.(spaceId)}
                  spaceId={spaceId}
                  spaceName={spaceName}
                />
              );
            })}
        </>
      )}
    </div>
  );
}
